import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Grid,
  Typography,
  useEventCallback,
} from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { ReactElement, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DocumentDeathUploadFormVM } from 'civil-status/view-models/document-birth-form';
import MatricolaList from '../shared/matricola-list';
import ListGender from 'shell/components/shared/gender';
import { DatePicker, TimePicker } from '@mui/lab';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { ConvertedImage } from 'shell/models/convert-image';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { Cropper } from 'shell/components/shared/cropper';
import SelectSerie from '../shared/select-serie';
import SelectRegistry from '../shared/select-registry';
import ListCivilStatus from '../shared/list-civil-status';
import { FormControlLabel } from '@mui/material';
import * as yup from 'yup';
import { commonSchemaObject } from 'civil-status/constants/common-schema';
import { deathSchemaObject } from '../../../shell/constants/create-schema-death';

const validationSchema = yup.object().shape({
  ...deathSchemaObject,
  ...commonSchemaObject,
});

interface FormDocumentDeathUploadNewProps {
  onOk: (form: DocumentDeathUploadFormVM) => void;
  onCancel: (form?: DocumentDeathUploadFormVM) => void;
  title: string;
  children?: ReactElement;
  disabled?: boolean;
  isEdit?: boolean;
}

const FormDocumentDeathUploadNew = ({
  onOk,
  onCancel,
  title,
  disabled = false,
}: FormDocumentDeathUploadNewProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const form = useFormik<DocumentDeathUploadFormVM>({
    initialValues: {
      year: '',
      registerNumber: '',
      documentNumber: '',
      portion: '',
      serie: '',
      serie2: '',
      matricola: '',
      surname: '',
      name: '',
      gender: '',
      deathPresumed: false,
      years: '',
      yearDate: '',
      deathDate: null,
      hourDeathDate: null,
      deathBy: null,
      deathTo: null,
      deathMunicipality: '',
      residenceMunicipality: '',
      birthday: null,
      municipalityBirth: '',
      status: '',
      spouseSurname: '',
      spouseName: '',
    },
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
  });

  const convertDocument = useHttpClient<HttpResponse<ConvertedImage>>(
    `${__CIVIL_STATUS_API_URL__}/convert-image`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      (
        convertDocument({
          data: {
            formato: 'png',
            immagineIniziale: (result as string)?.split(',')[1],
          },
        }) as ResponsePromise<HttpResponse<ConvertedImage>>
      ).then((response) => {
        form.setFieldValue('upload', {
          file,
          raw: response?.data?.payload?.immagineConvertita
            ? (response?.data?.payload?.immagineConvertita as Array<string>)?.map(
                (immagine: string) => `data:image/png;base64,${immagine}`
              )
            : [],
          rawTiff: result as string,
        });
      });
    });

    reader.readAsDataURL(file);
  });

  const handleCivilStatusListChange = useEventCallback((element: string | undefined) => {
    form.setFieldValue('status', element);
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={form.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Anno"
                    aria-label="Anno"
                    id="year"
                    fullWidth
                    inputProps={{
                      maxLength: '4',
                    }}
                    onChange={(event) => {
                      form.setFieldValue('year', event?.target.value);
                    }}
                    value={form.values.year || ''}
                    helperText={form.errors.year}
                    error={form.touched.year && Boolean(form.errors.year)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Atto"
                    aria-label="Atto"
                    fullWidth
                    id="document"
                    onChange={(event) => {
                      form.setFieldValue('documentNumber', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '4',
                    }}
                    value={form.values.documentNumber || ''}
                    helperText={form.errors.documentNumber}
                    error={form.touched.documentNumber && Boolean(form.errors.documentNumber)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectRegistry
                    isSmallSize
                    label="Registro"
                    id="registry"
                    onChange={(event) => {
                      form.setFieldValue('registerNumber', event?.target.value);
                    }}
                    value={form.values.registerNumber || ''}
                    error={form.touched.registerNumber && Boolean(form.errors.registerNumber)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Parte"
                    aria-label="Parte"
                    fullWidth
                    id="portion"
                    onChange={(event) => {
                      form.setFieldValue('portion', event?.target.value);
                    }}
                    inputProps={{
                      maxLength: '2',
                    }}
                    value={form.values.portion || ''}
                    helperText={form.errors.portion}
                    error={form.touched.portion && Boolean(form.errors.portion)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={2} style={{ paddingTop: '0px' }}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <SelectSerie
                    isSmallSize
                    label="Serie"
                    id="serie"
                    onChange={(event) => {
                      form.setFieldValue('serie', event?.target.value);
                    }}
                    value={form.values.serie || ''}
                    error={form.touched.serie && Boolean(form.errors.serie)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                <MatricolaList
                  aria-label="Matricola"
                  label="Matricola"
                  id="matricola"
                  disabled={disabled}
                  surname={form.values?.surname || ''}
                  name={form.values?.name || ''}
                  value={form.values?.matricola || ''}
                  dateBirth={form.values?.birthday || null}
                  onChanges={(value) =>
                    form.setValues({
                      ...form.values,
                      ...value,
                    })
                  }
                  error={form.touched?.matricola && Boolean(form.errors?.matricola)}
                  isSmallSize
                />
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Cognome"
                    label={`Cognome`}
                    id="surname"
                    name="surname"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.surname || ''}
                    onChange={(event) => form.setFieldValue('surname', event?.target.value)}
                    error={form.touched?.surname && Boolean(form.errors?.surname)}
                    helperText={form.errors?.surname}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Nome"
                    label={`Nome`}
                    id="name"
                    name="name"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.name || ''}
                    onChange={(event) => form.setFieldValue('name', event?.target.value)}
                    error={form.touched?.name && Boolean(form.errors?.name)}
                    helperText={form.errors?.name}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={7}>
                  <ListGender
                    value={form.values?.gender || ''}
                    onChange={(event) => form.setFieldValue('gender', event?.target.value)}
                    error={form.touched?.gender && Boolean(form.errors?.gender)}
                    disabled={disabled}
                    isSmallSize
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="deathPresumed"
                        checked={form.values?.deathPresumed || false}
                        onChange={() =>
                          form.setValues({
                            ...form.values,
                            deathDate: null,
                            hourDeathDate: null,
                            deathBy: null,
                            deathTo: null,
                            deathPresumed: !form.values?.deathPresumed,
                          })
                        }
                        inputProps={{ 'aria-label': 'morte presunta' }}
                      />
                    }
                    label="Morte Presunta"
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
                  <DatePicker
                    label="Data morte"
                    aria-label="Data morte"
                    value={form.values?.deathDate}
                    disabled={disabled || Boolean(form.values?.deathBy || form.values?.deathTo)}
                    onChange={(value) =>
                      form.setValues({
                        ...form.values,
                        deathDate: value,
                        deathBy: null,
                        deathTo: null,
                      })
                    }
                    renderInput={(params) => (
                      <PmiTextField
                        id="deathDate"
                        name="deathDate"
                        {...params}
                        fullWidth
                        error={form.touched?.deathDate && Boolean(form.errors?.deathDate)}
                        helperText={form.errors?.deathDate}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
                  <TimePicker
                    label="Ora morte"
                    value={form.values?.hourDeathDate}
                    disabled={disabled}
                    onChange={(value) => form.setFieldValue('hourDeathDate', value)}
                    renderInput={(params) => (
                      <PmiTextField
                        id="hourDeathDate"
                        name="hourDeathDate"
                        {...params}
                        fullWidth
                        error={form.touched?.hourDeathDate && Boolean(form.errors?.hourDeathDate)}
                        helperText={form.errors?.hourDeathDate}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
                  <DatePicker
                    label="Morto tra"
                    aria-label="Morto tra"
                    value={form.values?.deathBy}
                    disabled={disabled || Boolean(form.values?.deathDate)}
                    onChange={(value) =>
                      form.setValues({
                        ...form.values,
                        deathDate: null,
                        hourDeathDate: null,
                        deathBy: value,
                      })
                    }
                    renderInput={(params) => (
                      <PmiTextField
                        id="deathBy"
                        name="deathBy"
                        {...params}
                        fullWidth
                        error={form.touched?.deathBy && Boolean(form.errors?.deathBy)}
                        helperText={form.errors?.deathBy}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
                  <DatePicker
                    label="e il"
                    aria-label="e il"
                    value={form.values?.deathTo}
                    disabled={disabled || Boolean(form.values?.deathDate)}
                    onChange={(value) =>
                      form.setValues({
                        ...form.values,
                        deathDate: null,
                        hourDeathDate: null,
                        deathTo: value,
                      })
                    }
                    renderInput={(params) => (
                      <PmiTextField
                        id="deathTo"
                        name="deathTo"
                        {...params}
                        fullWidth
                        error={form.touched?.deathTo && Boolean(form.errors?.deathTo)}
                        helperText={form.errors?.deathTo}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune morte"
                    label="Comune morte"
                    id="deathMunicipality"
                    name="deathMunicipality"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.deathMunicipality || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('deathMunicipality', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('deathMunicipality', event?.target.value);
                    }}
                    error={
                      form.touched?.deathMunicipality && Boolean(form.errors?.deathMunicipality)
                    }
                    helperText={form.errors?.deathMunicipality}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={9} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune residenza"
                    label="Comune residenza"
                    id="residenceMunicipality"
                    name="residenceMunicipality"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.residenceMunicipality || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('residenceMunicipality', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('residenceMunicipality', event?.target.value);
                    }}
                    error={
                      form.touched?.residenceMunicipality &&
                      Boolean(form.errors?.residenceMunicipality)
                    }
                    helperText={form.errors?.residenceMunicipality}
                  />
                </Grid>
                <Grid item xs={12} md={3} style={{ paddingTop: '10px' }}>
                  <span>F2 per Milano</span>
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={4} style={{ paddingTop: '10px' }}>
                  <DatePicker
                    label="Data nascita"
                    aria-label="Data nascita"
                    value={form.values?.birthday || null}
                    disabled={disabled}
                    onChange={(value) => form.setFieldValue('birthday', value)}
                    renderInput={(params) => (
                      <PmiTextField
                        id="birthday"
                        name="birthday"
                        {...params}
                        fullWidth
                        error={form.touched?.birthday && Boolean(form.errors?.birthday)}
                        helperText={form.errors?.birthday}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Anni"
                    label="Anni"
                    id="year"
                    name="years"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.years || ''}
                    onChange={(event) => form.setFieldValue('years', event?.target.value)}
                    error={form.touched?.years && Boolean(form.errors?.years)}
                    helperText={form.errors?.years}
                  />
                </Grid>
                <Grid item xs={12} md={4} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Anno nascita"
                    label="Anno nascita"
                    id="year-birth"
                    name="year-birth"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.yearDate || ''}
                    onChange={(event) => form.setFieldValue('yearDate', event?.target.value)}
                    error={form.touched?.yearDate && Boolean(form.errors?.yearDate)}
                    helperText={form.errors?.yearDate}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} md={7} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    aria-label="Comune nascita"
                    label="Comune nascita"
                    id="municipalityBirth"
                    name="municipalityBirth"
                    fullWidth
                    disabled={disabled}
                    value={form.values?.municipalityBirth || ''}
                    onKeyDown={(event) => {
                      if (event.code === 'F2') {
                        event.preventDefault();
                        event.stopPropagation();
                        form.setFieldValue('municipalityBirth', 'MILANO');
                        return;
                      }
                    }}
                    onChange={(event) => {
                      form.setFieldValue('municipalityBirth', event?.target.value);
                    }}
                    error={
                      form.touched?.municipalityBirth && Boolean(form.errors?.municipalityBirth)
                    }
                    helperText={form.errors?.municipalityBirth}
                  />
                </Grid>
                <Grid item xs={12} md={5} style={{ paddingTop: '10px' }}>
                  <ListCivilStatus
                    id="civil-status"
                    value={form.values?.status || ''}
                    disabled={disabled}
                    onChange={(event) => handleCivilStatusListChange(event.target.value)}
                    error={form.touched?.status && Boolean(form.errors?.status)}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Coniuge - Cognome"
                    aria-label="Coniuge - Cognome"
                    id="spouseSurname"
                    name="spouseSurname"
                    fullWidth
                    value={form.values?.spouseSurname || ''}
                    disabled={disabled}
                    onChange={(event) => form.setFieldValue('spouseSurname', event?.target.value)}
                    error={form.touched?.spouseSurname && Boolean(form.errors?.spouseSurname)}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12} style={{ paddingTop: '10px' }}>
                  <PmiTextField
                    label="Coniuge - Nome"
                    aria-label="Coniuge - Nome"
                    id="spouseName"
                    name="spouseName"
                    fullWidth
                    value={form.values?.spouseName || ''}
                    disabled={disabled}
                    onChange={(event) => form.setFieldValue('spouseName', event?.target.value)}
                    error={form.touched?.spouseName && Boolean(form.errors?.spouseName)}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                Carica documento&nbsp;
                <PmiInputFile
                  accept="image/tiff"
                  multiple={false}
                  file={form.values.upload?.file}
                  showFilename
                  onChange={handleAddFile}
                  color="cdmBrandThird"
                  tipMaxSize={300}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={8}>
              <StickyDiv>
                <Cropper
                  images={form.values.upload?.raw || []}
                  disabled={!form.values?.upload?.raw?.length}
                />
              </StickyDiv>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(form.values);
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormDocumentDeathUploadNew.displayName = 'Form Document Birth new component';

export default FormDocumentDeathUploadNew;
